import React, { useEffect, useState, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { 
    Container, Grid, Typography, Box, Button, Paper, Divider, 
    TextField, Avatar, useTheme, alpha, Alert 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMapMarkerAlt, faReceipt, faCheckCircle, faWallet, 
    faExclamationTriangle, faArrowRight, faStore
} from '@fortawesome/free-solid-svg-icons';

// --- NUEVOS IMPORTS DE GOOGLE MAPS ---
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';

// Contextos y Controladores
import UserContext from '../../../navigation/context/userContext'; 
import { getMyCart } from '../../../controllers/commerce/CartController';
import { createMarketplaceOrders } from '../../../controllers/commerce/CheckoutController';
import { calculateDeliveryQuotes } from '../../../controllers/Logistics/DeliveryController';

// Componentes
import CustomBreadcrumbs from '../../../components/breadcrumbs';
import QUDProgress from '../../../components/progress'; 
import ResponseComponent from '../../../components/responseComponent'; 
import MultiOrderPaymentContainer from '../../../components/payments/MultiOrderPaymentContainer'; 
import AddressAutocomplete from '../../../components/maps/AddressAutocomplete';

export default function CheckoutPage() {
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();
    const { userData } = useContext(UserContext); 

    // Recibimos los ítems seleccionados desde el Carrito
    const selectedItemIds = location.state?.selectedItemIds || [];

    // --- ESTADOS ---
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [cartData, setCartData] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0.00); 
    const [createdOrderData, setCreatedOrderData] = useState(null);

    // --- ESTADOS LOGÍSTICOS ---
    const [logistics, setLogistics] = useState({});
    const [mapCenter, setMapCenter] = useState({ lat: 10.1917, lng: -64.6841 });
    const [mapLocation, setMapLocation] = useState({ lat: 10.1917, lng: -64.6841 });
    const [userHasSetLocation, setUserHasSetLocation] = useState(false);
    const [deliveryQuotes, setDeliveryQuotes] = useState({});
    
    // Verifica si al menos un local requiere delivery
    const needsDelivery = Object.values(logistics).includes('delivery');

    // Formulario de dirección manual
    const [formData, setFormData] = useState({
        address: '', city: '', zip: '', notes: ''
    });

    const [feedback, setFeedback] = useState({ show: false, success: false, title: '', message: '' });

    // 1. Cargar Datos Iniciales
    useEffect(() => {
        if (selectedItemIds.length === 0) {
            history.replace('/cart');
            return;
        }
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const res = await getMyCart();
        
        if (res.success && res.data && res.data.groups?.length > 0) {
            setCartData(res.data);
            
            const initialLogistics = {};
            res.data.groups.forEach(group => {
                const hasSelectedItems = group.items.some(item => selectedItemIds.includes(item.item_id));
                if (hasSelectedItems) {
                    initialLogistics[group.merchant_id] = 'pickup';
                }
            });
            setLogistics(initialLogistics);

            if (userData?.user) {
                setFormData(prev => ({ ...prev, address: userData.user.address || '' }));
                setWalletBalance(parseFloat(userData.user.wallet_balance || 0)); 
            }
        } else {
            history.replace('/cart'); 
        }
        setLoading(false);
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // --- FUNCIÓN PARA COTIZAR FLETES ---
    const updateDeliveryQuotes = async (currentLat, currentLng, currentLogistics) => {
        const merchantsForDelivery = Object.keys(currentLogistics)
            .filter(merchantId => currentLogistics[merchantId] === 'delivery')
            .map(id => parseInt(id));

        if (merchantsForDelivery.length === 0) {
            setDeliveryQuotes({});
            return;
        }

        const res = await calculateDeliveryQuotes(currentLat, currentLng, merchantsForDelivery);

        if (res.success) {
            const quotes = res.data.quotes;
            const newQuotesState = {};
            
            Object.keys(quotes).forEach(mId => {
                if (quotes[mId].success) {
                    newQuotesState[mId] = quotes[mId].fee;
                }
            });
            
            setDeliveryQuotes(newQuotesState);
        }
    };

    // Manejador del clic en el mapa
    const handleMapClick = (e) => {
        const newLat = e.detail.latLng.lat;
        const newLng = e.detail.latLng.lng;
        setMapLocation({ lat: newLat, lng: newLng });

        setUserHasSetLocation(true); // 🔥 Activa la bandera
        updateDeliveryQuotes(newLat, newLng, logistics);
        fetchAddressFromCoords(newLat, newLng); 
    };

    // Traducción de coordenadas a texto
    const fetchAddressFromCoords = (lat, lng) => {
        if (!window.google || !window.google.maps) return;
        
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
                setFormData(prev => ({ ...prev, address: results[0].formatted_address }));
            }
        });
    };


    // Manejador de botones Pickup/Delivery
    const handleLogisticsChange = (merchantId, type) => {
        const newLogistics = { ...logistics, [merchantId]: type };
        setLogistics(newLogistics);
        
        // 🔥 Solo calculamos el flete si el usuario ya interactuó con el mapa
        if (userHasSetLocation) {
            updateDeliveryQuotes(mapLocation.lat, mapLocation.lng, newLogistics);
        } else {
            // Si desmarcan delivery o cambian antes de poner el pin, limpiamos la cotización
            setDeliveryQuotes({});
        }
    };

    const handleGenerateOrders = async () => {
        if (needsDelivery && (!formData.address || !userHasSetLocation)) {
            setFeedback({ show: true, success: false, title: 'Ubicación requerida', message: 'Por favor busca tu dirección o coloca el pin en el mapa.' });
            return;
        }

        setProcessing(true);

        const finalShippingData = {
            ...formData,
            lat: needsDelivery ? mapLocation.lat : null,
            lng: needsDelivery ? mapLocation.lng : null,
            logistics_preferences: logistics
        };

        const response = await createMarketplaceOrders({
            shipping_address: finalShippingData,
            cart_uuid: cartData.uuid,
            item_ids: selectedItemIds 
        });

        if (response.success) {
            setCreatedOrderData(response.data); 
            window.scrollTo(0,0);
        } else {
            setFeedback({ show: true, success: false, title: 'Error', message: response.message });
        }
        setProcessing(false);
    };

    const handlePayWithWallet = async () => {
        if(!window.confirm("¿Confirmar pago con tu saldo disponible?")) return;
        // Lógica de pago
    };

    const handleRechargeSuccess = (data) => {
        if(data) {
             history.push('/orders/marketplace');
        }
    };

    if (loading) return <QUDProgress open={true} />;

    // === CÁLCULO DINÁMICO DEL RESUMEN ===
    let calcSubtotal = 0;
    let calcDelivery = 0;
    let activeCurrencySymbol = '$';
    let activeCurrencyCode = 'USD';

    if (cartData) {
        cartData.groups.forEach(group => {
            const selectedInGroup = group.items.filter(item => selectedItemIds.includes(item.item_id));
            if (selectedInGroup.length > 0) {
                activeCurrencySymbol = group.currency_symbol || '$';
                activeCurrencyCode = group.currency_code || 'USD';
                
                const groupSub = selectedInGroup.reduce((acc, item) => acc + (parseFloat(item.total_line) || 0), 0);
                calcSubtotal += groupSub;
                calcDelivery += parseFloat(deliveryQuotes[group.merchant_id] || 0);
            }
        });
    }

    const summaryTotalToPay = calcSubtotal + calcDelivery;

    if (createdOrderData) {
        const totalToPay = parseFloat(createdOrderData.total_to_pay);
        const missingAmount = totalToPay - walletBalance;
        const needsRecharge = missingAmount > 0;
        const finalCurrency = createdOrderData.currency || activeCurrencyCode;

        return (
            <Container maxWidth="md" sx={{ pt: 6, pb: 8 }}>
                <Paper elevation={0} sx={{ p: {xs: 2, md: 4}, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Box textAlign="center" mb={4}>
                        <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.lighter', color: 'primary.main', mx: 'auto', mb: 2 }}>
                            <FontAwesomeIcon icon={faReceipt} size="lg" />
                        </Avatar>
                        <Typography variant="h5" fontWeight="bold">Pedidos Generados</Typography>
                    </Box>

                    <Box sx={{ bgcolor: '#f8f9fa', p: 3, borderRadius: 2, mb: 4 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">Total a Pagar ({finalCurrency})</Typography>
                                <Typography variant="h4" fontWeight="bold" color="text.primary">
                                    {activeCurrencySymbol}{totalToPay.toFixed(2)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ textAlign: {sm: 'right'} }}>
                                <Typography variant="body2" color="text.secondary">Tu Saldo Disponible</Typography>
                                <Box display="flex" alignItems="center" justifyContent={{xs:'flex-start', sm:'flex-end'}} gap={1}>
                                    <FontAwesomeIcon icon={faWallet} color="#555"/>
                                    <Typography variant="h6" fontWeight="bold">
                                        {activeCurrencySymbol}{walletBalance.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {needsRecharge ? (
                            <Alert severity="warning" icon={<FontAwesomeIcon icon={faExclamationTriangle}/>}>
                                <strong>Saldo Insuficiente.</strong> Necesitas recargar <strong>{activeCurrencySymbol}{missingAmount.toFixed(2)}</strong>.
                            </Alert>
                        ) : (
                            <Alert severity="success" icon={<FontAwesomeIcon icon={faCheckCircle}/>}>
                                <strong>¡Todo listo!</strong> Tienes saldo suficiente para cubrir esta compra.
                            </Alert>
                        )}
                    </Box>

                    <Box>
                        {needsRecharge ? (
                            <Box>
                                <Typography variant="h6" gutterBottom fontWeight="bold">Recargar Diferencia</Typography>
                                <MultiOrderPaymentContainer 
                                    paymentData={{
                                        total_to_pay: missingAmount,
                                        orders: createdOrderData.orders 
                                    }}
                                    merchantUuid="QUD" 
                                    customer={{
                                        nombre: userData.entity.name,
                                        apellido: userData.entity.last_name,
                                        documento_tipo: userData.entity.document_type,
                                        documento: userData.entity.document_number,
                                        correo:userData.user.email,
                                        prefijo:userData.user.prefijo,
                                        telefono: userData.entity.phone,
                                        profesion: userData.entity.profession
                                    }}
                                    onPaymentSuccess={handleRechargeSuccess}
                                />
                            </Box>
                        ) : (
                            <Box textAlign="center" mt={2}>
                                <Button 
                                    variant="contained" size="large" onClick={handlePayWithWallet} disabled={processing}
                                    sx={{ borderRadius: 50, px: 6, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', boxShadow: '0 10px 25px rgba(0,118,255,0.3)' }}
                                >
                                    {processing ? 'Procesando...' : 'Pagar con mi Saldo'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Container>
        );
    }

    // === VISTA A: FORMULARIO INICIAL DE CONFIGURACIÓN ===
    return (
        <Container maxWidth="lg" sx={{ pt: 4, pb: 10 }}>
            <CustomBreadcrumbs returnTo='/cart' crumbs={[{ name: 'Carrito', link: '/cart' }, { name: 'Checkout', link: '/checkout' }]} />
            
            <Typography variant="h4" fontWeight="800" sx={{ mt: 2, mb: 4, color: 'text.primary', letterSpacing: '-0.5px' }}>
                Opciones de Envío
            </Typography>

            <Grid container spacing={4}>
                
                {/* IZQUIERDA: CONFIGURACIÓN LOGÍSTICA */}
                <Grid item xs={12} md={7}>
                    
                    {/* 1. ACORDEÓN POR LOCAL */}
                    {cartData?.groups.map(group => {
                        const hasSelectedItems = group.items.some(item => selectedItemIds.includes(item.item_id));
                        if (!hasSelectedItems) return null;

                        const isDelivery = logistics[group.merchant_id] === 'delivery';

                        return (
                            <Paper 
                                key={group.merchant_id} 
                                elevation={0} 
                                sx={{ 
                                    p: { xs: 2, sm: 3.5 }, 
                                    mb: 3, 
                                    borderRadius: 4,
                                    border: '1px solid',
                                    borderColor: 'grey.100',
                                    boxShadow: '0 4px 24px rgba(0,0,0,0.02)'
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                                        <FontAwesomeIcon icon={faStore} size="sm" />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight="700">
                                        Envío para {group.merchant_name}
                                    </Typography>
                                </Box>
                                
                                <Grid container spacing={2}>
                                    {/* Tarjeta Pickup */}
                                    <Grid item xs={12} sm={6}>
                                        <Box 
                                            onClick={() => handleLogisticsChange(group.merchant_id, 'pickup')}
                                            sx={{ 
                                                p: 2, 
                                                borderRadius: 3, 
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: !isDelivery ? 'primary.main' : 'grey.200',
                                                bgcolor: !isDelivery ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
                                                transition: 'all 0.2s ease',
                                                '&:hover': { borderColor: !isDelivery ? 'primary.main' : 'grey.300' }
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight="700" color={!isDelivery ? 'primary.main' : 'text.primary'}>
                                                🏪 Retirar en Local
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                                Sin costo adicional ($0.00)
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* 🔥 TARJETA DELIVERY CORREGIDA Y DINÁMICA 🔥 */}
                                    <Grid item xs={12} sm={6}>
                                        <Box 
                                            onClick={() => handleLogisticsChange(group.merchant_id, 'delivery')}
                                            sx={{ 
                                                p: 2, 
                                                borderRadius: 3, 
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: isDelivery ? 'primary.main' : 'grey.200',
                                                bgcolor: isDelivery ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
                                                transition: 'all 0.2s ease',
                                                '&:hover': { borderColor: isDelivery ? 'primary.main' : 'grey.300' }
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle1" fontWeight="700" color={isDelivery ? 'primary.main' : 'text.primary'}>
                                                    🛵 Delivery
                                                </Typography>
                                                
                                                {/* Aquí se muestra el precio real cuando el backend lo calcula */}
                                                {deliveryQuotes[group.merchant_id] !== undefined && (
                                                    <Typography variant="subtitle2" fontWeight="800" color="primary.main">
                                                        +{group.currency_symbol || '$'}{deliveryQuotes[group.merchant_id].toFixed(2)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            
                                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                                {deliveryQuotes[group.merchant_id] !== undefined 
                                                    ? 'Costo exacto a tu ubicación' 
                                                    : 'Tarifa según distancia'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        );
                    })}

                    {/* 2. MAPA INTERACTIVO */}
                    {needsDelivery && (
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: { xs: 2, sm: 3.5 }, 
                                borderRadius: 4,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`
                            }}
                        >
                            <Box display="flex" alignItems="flex-start" gap={2} mb={3}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="700" lineHeight={1.2}>¿A dónde lo enviamos?</Typography>
                                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                                        Busca tu dirección o arrastra el pin rojo a tu ubicación exacta.
                                    </Typography>
                                </Box>
                            </Box>

                            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "TU_API_KEY"}>
                                
                                <AddressAutocomplete 
                                    onPlaceSelected={(place) => {
                                        if (place.geometry?.location) {
                                            const newLat = place.geometry.location.lat();
                                            const newLng = place.geometry.location.lng();

                                            setMapCenter({ lat: newLat, lng: newLng });
                                            setMapLocation({ lat: newLat, lng: newLng });

                                            setUserHasSetLocation(true); // 🔥 Activa la bandera
                                            updateDeliveryQuotes(newLat, newLng, logistics);

                                            if (place.formatted_address) {
                                                setFormData(prev => ({ ...prev, address: place.formatted_address }));
                                            }
                                        }
                                    }}
                                />

                                <Box sx={{ width: '100%', height: 350, borderRadius: 3, overflow: 'hidden', mb: 3, border: '1px solid', borderColor: 'grey.200' }}>
                                    <Map 
                                        defaultZoom={16} 
                                        center={mapCenter} 
                                        onCameraChanged={(ev) => setMapCenter(ev.detail.center)} 
                                        onClick={handleMapClick}
                                        mapId="QUD_DELIVERY_MAP" 
                                        disableDefaultUI={true}
                                        gestureHandling="greedy" 
                                    >
                                        <AdvancedMarker 
                                            position={mapLocation} 
                                            draggable={true} 
                                            onDragEnd={(e) => {
                                                const newLat = e.latLng.lat();
                                                const newLng = e.latLng.lng();

                                                setMapLocation({ lat: newLat, lng: newLng });

                                                setUserHasSetLocation(true); // 🔥 Activa la bandera
                                                updateDeliveryQuotes(newLat, newLng, logistics);
                                                fetchAddressFromCoords(newLat, newLng); 
                                            }}
                                        />
                                    </Map>
                                </Box>
                            </APIProvider>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth required label="Dirección" name="address" 
                                        value={formData.address} onChange={handleInputChange}
                                        placeholder="Ej: Apto 4B, Piso 2, Torre A..."
                                        variant="filled"
                                        InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth multiline rows={2} label="Punto de referencia" name="notes" 
                                        value={formData.notes} onChange={handleInputChange} 
                                        placeholder="Ej: Casa blanca con rejas negras."
                                        variant="filled"
                                        InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    )}
                </Grid>

                {/* DERECHA: RESUMEN DE PAGO */}
                <Grid item xs={12} md={5}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: { xs: 3, sm: 4 }, 
                            bgcolor: '#fff', 
                            borderRadius: 4, 
                            border: '1px solid', 
                            borderColor: 'grey.100',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.03)',
                            position: 'sticky', 
                            top: 100 
                        }}
                    >
                        <Typography variant="h6" fontWeight="800" mb={4}>Resumen de Pago</Typography>

                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <Typography color="text.secondary">Ítems a procesar</Typography>
                            <Typography fontWeight="600">{selectedItemIds.length}</Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <Typography color="text.secondary">Subtotal</Typography>
                            <Typography fontWeight="600">{activeCurrencySymbol}{calcSubtotal.toFixed(2)}</Typography>
                        </Box>

                        {calcDelivery > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Typography color="text.secondary">Flete Combinado</Typography>
                                <Typography fontWeight="600">{activeCurrencySymbol}{calcDelivery.toFixed(2)}</Typography>
                            </Box>
                        )}
                        
                        <Divider sx={{ my: 3, borderStyle: 'dashed', borderColor: 'grey.300' }} />

                        <Box display="flex" justifyContent="space-between" mb={4} alignItems="flex-end">
                            <Typography variant="h6" fontWeight="700">Total</Typography>
                            <Box textAlign="right">
                                <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" mb={0.5}>
                                    Moneda Local ({activeCurrencyCode})
                                </Typography>
                                <Typography variant="h3" fontWeight="800" color="primary.main" sx={{ lineHeight: 1, letterSpacing: '-1px' }}>
                                    {activeCurrencySymbol}{summaryTotalToPay.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>

                        <Button 
                            fullWidth 
                            variant="contained" 
                            size="large" 
                            onClick={handleGenerateOrders} 
                            disabled={processing}
                            endIcon={!processing && <FontAwesomeIcon icon={faArrowRight} />}
                            sx={{ 
                                borderRadius: 50, 
                                py: 1.8, 
                                fontWeight: '700',
                                fontSize: '1.05rem',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                                '&:hover': { boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}` }
                            }}
                        >
                            {processing ? 'Procesando...' : 'Confirmar y Pagar'}
                        </Button>
                    </Paper>
                </Grid>
            </Grid>

            <QUDProgress open={processing} />
            <ResponseComponent 
                show={feedback.show} success={feedback.success} title={feedback.title} message={feedback.message} 
                onClick={() => setFeedback({ ...feedback, show: false })}
            />
        </Container>
    );
}